/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC boundary; see mem://preferences/lint-debt.md */
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LibraryItemEditor } from "@/components/library/LibraryItemEditor";
import { AddSourceSearchPanel } from "@/components/library/addSource/AddSourceSearchPanel";
import {
  deriveLibraryTitle,
  emptyLibraryDraft,
  fetchLibraryItemDecryptedText,
  fetchLibraryItems,
  findLikelyDuplicateLibraryItems,
  libraryItemToDraft,
  normalizeLibraryDraft,
  saveLibraryItem,
  searchLibrarySources,
  type LibraryItem,
  type LibrarySearchResult,
  type LibrarySourceType,
} from "@/lib/library";
import type { DetectedSourceIdentifier } from "@/lib/sourceIdentifiers";

type EditableDraft = LibrarySearchResult & { notes?: string | null };

export type AddSourceInlineDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  initialQuery?: string;
  initialIdentifier?: DetectedSourceIdentifier | null;
  /** When provided, the dialog opens in edit mode for this existing library item. */
  editingItem?: LibraryItem | null;
  existingItems?: LibraryItem[];
  /** Called for both create and edit (in edit mode this is the updated row). */
  onCreated: (item: LibraryItem) => void;
};

const applyIdentifierToDraft = (
  draft: EditableDraft,
  identifier: DetectedSourceIdentifier,
): EditableDraft => {
  const next: EditableDraft = { ...draft, sourceType: identifier.suggestedType };
  if (identifier.kind === "doi") next.doi = identifier.value;
  if (identifier.kind === "url") next.url = identifier.value;
  if (identifier.kind === "isbn") {
    if (identifier.value.length === 13) next.isbn13 = identifier.value;
    else next.isbn10 = identifier.value;
  }
  return next;
};

export const AddSourceInlineDialog = ({
  open,
  onOpenChange,
  workspaceId,
  initialQuery = "",
  initialIdentifier = null,
  editingItem = null,
  existingItems,
  onCreated,
}: AddSourceInlineDialogProps) => {
  const isEditMode = !!editingItem;
  const [tab, setTab] = useState<"search" | "manual">("search");
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<LibrarySearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [draft, setDraft] = useState<EditableDraft>(() => ({ ...emptyLibraryDraft(), notes: null }));
  const [saving, setSaving] = useState(false);
  const [autoLookupRunning, setAutoLookupRunning] = useState(false);
  const lastIdentifierKey = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      lastIdentifierKey.current = null;
      return;
    }
    setQuery(initialQuery);
    setResults([]);

    // Edit mode wins over identifier-based create.
    if (editingItem) {
      // Seed from the in-memory record (which may carry only a 280-char preview),
      // then immediately overlay the freshly-decrypted full text so the Synopsis
      // textarea has the real value before the user can save.
      setDraft(libraryItemToDraft(editingItem));
      setTab("manual");
      lastIdentifierKey.current = `edit:${editingItem.id}`;
      const itemId = editingItem.id;
      const wsId = editingItem.workspace_id;
      void fetchLibraryItemDecryptedText(wsId, itemId).then(({ notes, description }) => {
        setDraft((current) => ({
          ...current,
          description: description ?? current.description ?? null,
          notes: notes ?? current.notes ?? null,
        }));
      });
      return;
    }

    let nextDraft: EditableDraft = { ...emptyLibraryDraft(), notes: null };
    if (initialIdentifier) {
      nextDraft = applyIdentifierToDraft(nextDraft, initialIdentifier);
    }
    setDraft(nextDraft);
    setTab(initialIdentifier ? "manual" : "search");

    if (initialIdentifier) {
      const key = `${initialIdentifier.kind}:${initialIdentifier.value}`;
      if (lastIdentifierKey.current === key) return;
      lastIdentifierKey.current = key;

      if (initialIdentifier.kind === "doi") {
        // Crossref autopopulate; user still confirms before save.
        (async () => {
          setAutoLookupRunning(true);
          try {
            const found = await searchLibrarySources(initialIdentifier.value, "journal_article");
            const exact = found.find((r) => r.doi?.toLowerCase() === initialIdentifier.value.toLowerCase());
            const pick = exact ?? found[0];
            if (pick) {
              setDraft((current) => ({
                ...pick,
                notes: current.notes ?? null,
                doi: pick.doi ?? initialIdentifier.value,
                sourceType: pick.sourceType ?? "journal_article",
              }));
              toast.success("Prefilled from DOI lookup — review and save.");
            } else {
              toast.message("No DOI match found — fill in manually below.");
            }
          } catch (err: any) {
            toast.error(err?.message ?? "DOI lookup failed");
          } finally {
            setAutoLookupRunning(false);
          }
        })();
      } else if (initialIdentifier.kind === "isbn") {
        (async () => {
          setAutoLookupRunning(true);
          try {
            const found = await searchLibrarySources(initialIdentifier.value, "book");
            const matchIsbn = (value: string | null) => value?.replace(/[^0-9X]/gi, "") === initialIdentifier.value;
            const exact = found.find((r) => matchIsbn(r.isbn13) || matchIsbn(r.isbn10));
            const pick = exact ?? found[0];
            if (pick) {
              setDraft((current) => ({
                ...pick,
                notes: current.notes ?? null,
                sourceType: "book" as LibrarySourceType,
              }));
              toast.success("Prefilled from ISBN lookup — review and save.");
            }
          } catch (err: any) {
            toast.error(err?.message ?? "ISBN lookup failed");
          } finally {
            setAutoLookupRunning(false);
          }
        })();
      }
    }
  }, [open, initialQuery, initialIdentifier, editingItem]);

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) {
      toast.error("Type at least 2 characters to search");
      return;
    }
    setSearching(true);
    try {
      const found = await searchLibrarySources(q);
      setResults(found);
      if (found.length === 0) toast.message("No matches — try the manual tab to add it yourself.");
    } catch (err: any) {
      toast.error(err?.message ?? "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const pickResult = (result: LibrarySearchResult) => {
    setDraft({ ...result, notes: null });
    setTab("manual");
  };

  const handleSave = async (force = false) => {
    const normalized = normalizeLibraryDraft(draft);
    if (!normalized.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      // Skip duplicate detection in edit mode — we are updating the very record
      // that would otherwise be flagged as a match against itself.
      if (!isEditMode && !force) {
        const items = existingItems ?? (await fetchLibraryItems(workspaceId));
        const warnings = findLikelyDuplicateLibraryItems(normalized, items);
        const strong = warnings.find((w) => w.severity === "strong");
        if (strong) {
          const existingTitle = deriveLibraryTitle({
            title: strong.item.title,
            subtitle: strong.item.subtitle,
            display_title: strong.item.display_title,
          });
          toast.error(`Already in My Library: “${existingTitle}”`, {
            description: `${strong.headline}. ${strong.detail}`,
            action: {
              label: "Save anyway",
              onClick: () => { void handleSave(true); },
            },
          });
          setSaving(false);
          return;
        }
      }
      const saved = await saveLibraryItem(workspaceId, normalized, isEditMode ? editingItem!.id : undefined);
      toast.success(isEditMode ? "Source updated" : "Source added to My Library");
      onCreated(saved);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message ?? "Could not save source");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit source" : "Add new source to My Library"}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update this library record. Linked excerpts will reflect the new author, title, and citation metadata."
              : "Search published works or fill out the details manually. The saved record becomes the citation source for this excerpt."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(value) => setTab(value as "search" | "manual")}>
          {!isEditMode && (
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="search">Search</TabsTrigger>
              <TabsTrigger value="manual">Manual entry</TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="search" className="space-y-4 pt-4">
            <AddSourceSearchPanel
              query={query}
              onQueryChange={setQuery}
              searching={searching}
              results={results}
              onSearch={runSearch}
              onPick={pickResult}
            />
          </TabsContent>

          <TabsContent value="manual" className="pt-4">
            {autoLookupRunning && (
              <div className="mb-3 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Looking up identifier — fields will populate automatically.
              </div>
            )}
            <LibraryItemEditor draft={draft} onChange={setDraft} idPrefix="add-source" workspaceId={workspaceId} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => handleSave()} disabled={saving || !draft.title.trim()}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {isEditMode ? "Save changes" : "Save to My Library"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};